<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="600" persistent scrollable>
    <v-card dark>
      <v-card-title>
        <v-icon left>mdi-share</v-icon>
        Bewerbung weiterleiten
        <v-spacer></v-spacer>
        <v-btn icon @click="closeDialog" :disabled="isSubmitting"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pt-4" style="max-height: 70vh">
        <v-alert type="info" text dense class="mb-4">
          Die aktuell zuständigen Personen verlieren den Zugriff. Die neuen Personen erhalten eine E-Mail und Push-Benachrichtigung.
        </v-alert>

        <v-card outlined color="rgba(255,255,255,0.05)" class="pa-3 mb-4">
          <div class="text-subtitle-2 mb-2">Zuständige Person hinzufügen</div>
          <v-autocomplete
            v-model="userToAdd"
            :items="availableUsers"
            :loading="isLoadingUsers"
            :item-text="userDisplayName"
            item-value="_id"
            return-object
            label="Mitarbeiter suchen..."
            placeholder="Name oder E-Mail eingeben"
            outlined
            dense
            hide-details
            clearable
            no-data-text="Keine berechtigten Mitarbeiter gefunden"
            @change="addUserToList">
            <template v-slot:item="{ item }">
              <v-list-item-avatar color="grey darken-3" size="32">
                <span class="ma-auto white--text caption"> {{ (item.vorname || "U").charAt(0) }}{{ (item.nachname || "").charAt(0) }} </span>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ item.vorname }} {{ item.nachname }}</v-list-item-title>
                <v-list-item-subtitle class="caption">{{ item.email }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
          <p class="caption grey--text mt-2 mb-0">Nur Mitarbeiter mit Berechtigung zur Bearbeitung von Bewerbungen werden angezeigt.</p>
        </v-card>

        <div class="text-subtitle-2 mb-2 grey--text">Zukünftig zuständig ({{ selectedUsers.length }})</div>

        <v-list dense color="transparent" class="mb-4">
          <v-list-item v-for="(user, index) in selectedUsers" :key="user._id" class="px-0">
            <v-list-item-avatar color="grey darken-3" size="32">
              <span class="ma-auto white--text caption"> {{ (user.vorname || "U").charAt(0) }}{{ (user.nachname || "").charAt(0) }} </span>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ user.vorname }} {{ user.nachname }}</v-list-item-title>
              <v-list-item-subtitle class="caption">{{ user.email }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn icon small color="red lighten-2" @click="removeUserFromList(index)">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
          <v-alert v-if="selectedUsers.length === 0" type="warning" text dense> Bitte wähle mindestens eine Person aus. </v-alert>
        </v-list>

        <v-textarea
          v-model="forwardReason"
          label="Begründung / Nachricht (Optional)"
          placeholder="Warum wird diese Bewerbung weitergeleitet?"
          outlined
          dense
          rows="3"
          hide-details></v-textarea>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="closeDialog" :disabled="isSubmitting">Abbrechen</v-btn>
        <v-btn color="primary" @click="submitForward" :loading="isSubmitting" :disabled="selectedUsers.length === 0">
          <v-icon left>mdi-send</v-icon>
          Weiterleiten
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/api"

export default {
  name: "JobApplicationForwardDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    application: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isSubmitting: false,
      isLoadingUsers: false,
      availableUsers: [],
      userToAdd: null,
      selectedUsers: [],
      forwardReason: "",
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) {
          this.resetState()
          this.loadEligibleUsers()
        }
      },
    },
  },
  methods: {
    resetState() {
      this.userToAdd = null
      this.selectedUsers = []
      this.forwardReason = ""
    },
    async loadEligibleUsers() {
      this.isLoadingUsers = true
      try {
        this.availableUsers = await api.jobApplications.fetchEligibleUsers()
      } catch (error) {
        console.error("Fehler beim Laden der berechtigten User:", error)
        if (this.$toast) this.$toast.error("Berechtigte Mitarbeiter konnten nicht geladen werden")
      } finally {
        this.isLoadingUsers = false
      }
    },
    userDisplayName(user) {
      if (!user) return ""
      return `${user.vorname || ""} ${user.nachname || ""} (${user.email || ""})`.trim()
    },
    addUserToList(user) {
      if (!user) return

      const alreadyAdded = this.selectedUsers.some((u) => u._id === user._id)
      if (alreadyAdded) {
        if (this.$toast) this.$toast.info("Person ist bereits in der Liste")
      } else {
        this.selectedUsers.push(user)
      }

      // Autocomplete leeren auf nextTick, damit der @change-Handler sauber durchläuft
      this.$nextTick(() => {
        this.userToAdd = null
      })
    },
    removeUserFromList(index) {
      this.selectedUsers.splice(index, 1)
    },
    closeDialog() {
      this.$emit("input", false)
    },
    async submitForward() {
      if (this.selectedUsers.length === 0) return

      this.isSubmitting = true
      try {
        const userIds = this.selectedUsers.map((user) => user._id)
        const updatedApplication = await api.jobApplications.forward(this.application._id, userIds, this.forwardReason)

        if (this.$toast) this.$toast.success("Bewerbung erfolgreich weitergeleitet")
        this.$emit("forwarded", updatedApplication)
      } catch (error) {
        console.error("Fehler beim Weiterleiten:", error)
        if (this.$toast) this.$toast.error("Bewerbung konnte nicht weitergeleitet werden")
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>
